import CloseRounded from '@mui/icons-material/CloseRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useWatch, type Control, type UseFormSetValue } from 'react-hook-form';
import { api, unwrap } from '../../api/client';
import { VariantPicker, type Variant } from '../../components/pickers';
import { money } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { trimZeros, type QuoteFormValues, type QuoteLineDraft } from './forms';
import { defaultSaleUnit, productQuery, unitOptions, type TaxRate } from './model';
import { LINE_GRID } from './lineGrid';
import { belowList, listPrice, type DiscountType, type LineResult } from './totals';

/** Small "% / Rs" switch used inside discount fields. */
export function DiscountTypeSwitch({
  value,
  onChange,
  currency,
}: {
  value: DiscountType;
  onChange: (v: DiscountType) => void;
  currency: string;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onChange(value === 'PERCENT' ? 'AMOUNT' : 'PERCENT')}
      aria-label={
        value === 'PERCENT'
          ? 'Discount in percent — switch to amount'
          : 'Discount as amount — switch to percent'
      }
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: '8px',
        bgcolor: 'action.hover',
        color: 'text.primary',
        font: 'inherit',
        fontSize: 12,
        fontWeight: 700,
        px: 0.9,
        py: 0.25,
        cursor: 'pointer',
        minWidth: 34,
        '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
      }}
    >
      {value === 'PERCENT' ? '%' : currency}
    </Box>
  );
}

export function QuoteLineRow({
  control,
  setValue,
  index,
  result,
  showErrors,
  taxEnabled,
  taxRates,
  canDiscount,
  canRemove,
  onRemove,
  autoFocus,
}: {
  control: Control<QuoteFormValues>;
  setValue: UseFormSetValue<QuoteFormValues>;
  index: number;
  result: LineResult | undefined;
  showErrors: boolean;
  taxEnabled: boolean;
  taxRates: TaxRate[];
  canDiscount: boolean;
  canRemove: boolean;
  onRemove: () => void;
  autoFocus?: boolean;
}) {
  const { format } = useShop();
  const queryClient = useQueryClient();
  const line = useWatch({ control, name: `lines.${index}` }) as QuoteLineDraft | undefined;
  const name = (f: keyof QuoteLineDraft) => `lines.${index}.${f}` as const;

  // Saved lines only carry the variant id: look the item up once for its units and price hints.
  const lookup = useQuery({
    queryKey: ['variants', 'lookup', line?.sku],
    queryFn: () =>
      unwrap(api.GET('/variants/lookup', { params: { query: { code: line?.sku ?? '' } } })),
    enabled: Boolean(line?.variantId && line.productId === null && line.sku),
    staleTime: 5 * 60_000,
    retry: false,
  });
  useEffect(() => {
    const v = lookup.data;
    if (!v || !line || line.productId !== null || v.id !== line.variantId) return;
    setValue(`lines.${index}`, {
      ...line,
      productId: v.productId,
      salePrice: v.salePrice,
      baseAllowsFraction: v.baseUnit.allowsFraction,
    });
  }, [lookup.data, line, index, setValue]);

  const product = useQuery({
    ...productQuery(line?.productId ?? ''),
    enabled: Boolean(line?.productId),
  });
  const units = product.data
    ? unitOptions(product.data)
    : line?.unitId
      ? [
          {
            unitId: line.unitId,
            symbol: line.unitSymbol,
            name: line.unitSymbol,
            factor: line.factor,
          },
        ]
      : [];

  if (!line) return null;

  const pick = async (v: Variant | null) => {
    if (!v) return;
    const next: QuoteLineDraft = {
      ...line,
      variantId: v.id,
      productId: v.productId,
      sku: v.sku,
      description: v.displayName,
      unitId: v.baseUnit.id,
      unitSymbol: v.baseUnit.symbol,
      factor: '1',
      baseAllowsFraction: v.baseUnit.allowsFraction,
      salePrice: v.salePrice,
      unitPrice: trimZeros(listPrice(v.salePrice, '1')),
      qty: line.qty || '1',
    };
    setValue(`lines.${index}`, next, { shouldDirty: true, shouldValidate: showErrors });
    try {
      const p = await queryClient.query(productQuery(v.productId));
      const unit = defaultSaleUnit(p);
      if (unit.unitId !== next.unitId)
        setValue(
          `lines.${index}`,
          {
            ...next,
            unitId: unit.unitId,
            unitSymbol: unit.symbol,
            factor: unit.factor,
            unitPrice: trimZeros(listPrice(v.salePrice, unit.factor)),
          },
          { shouldDirty: true },
        );
    } catch {
      // Keep the base unit; the API applies the default sale unit's price rules anyway.
    }
  };

  const changeUnit = (unitId: string) => {
    const u = units.find((o) => o.unitId === unitId);
    if (!u) return;
    // Keep a hand-typed price; re-price only when the line still shows the old list price.
    const untouched =
      line.salePrice !== null &&
      Number(line.unitPrice) === Number(listPrice(line.salePrice, line.factor));
    setValue(
      `lines.${index}`,
      {
        ...line,
        unitId: u.unitId,
        unitSymbol: u.symbol,
        factor: u.factor,
        unitPrice:
          untouched && line.salePrice !== null
            ? trimZeros(listPrice(line.salePrice, u.factor))
            : line.unitPrice,
      },
      { shouldDirty: true },
    );
  };

  const list = line.salePrice !== null ? listPrice(line.salePrice, line.factor) : null;
  const priceBelow = list !== null && belowList(line.unitPrice, list);
  const discounted = Number(result?.lineDiscount ?? 0) + Number(result?.documentDiscount ?? 0) > 0;
  const needsPermission = !canDiscount && (priceBelow || Number(line.discountValue || 0) > 0);
  // Live checks once an item is on the line; everything after the first save attempt.
  const err = showErrors || line.variantId ? result?.errors : undefined;
  const currency = money(0, { ...format, currencyDecimals: 0 }).replace(/[\d\s.,]/g, '') || '¤';

  return (
    <Box sx={{ ...LINE_GRID, py: 1.75 }}>
      <Typography
        className="tabular"
        sx={{
          gridArea: 'num',
          display: { xs: 'none', md: 'block' },
          pt: 1.1,
          color: 'text.disabled',
          fontWeight: 600,
        }}
      >
        {index + 1}
      </Typography>

      <Box sx={{ gridArea: 'item', minWidth: 0 }}>
        {line.variantId ? (
          <Controller
            control={control}
            name={name('description')}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                value={field.value}
                label={`Item ${String(index + 1)}`}
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? `SKU ${line.sku}`}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Choose another item">
                          <IconButton
                            size="small"
                            edge="end"
                            aria-label={`Change item on line ${String(index + 1)}`}
                            onClick={() =>
                              setValue(
                                `lines.${index}`,
                                {
                                  ...line,
                                  variantId: '',
                                  productId: null,
                                  sku: '',
                                  description: '',
                                  unitId: '',
                                  salePrice: null,
                                  unitPrice: '',
                                },
                                { shouldDirty: true },
                              )
                            }
                          >
                            <CloseRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        ) : (
          <Controller
            control={control}
            name={name('description')}
            render={({ fieldState }) => (
              <Box>
                <VariantPicker
                  value={null}
                  onChange={(v) => void pick(v)}
                  label={`Item ${String(index + 1)}`}
                  autoFocus={autoFocus}
                />
                {fieldState.error && (
                  <Typography variant="caption" color="error" sx={{ ml: 1.75 }}>
                    {fieldState.error.message}
                  </Typography>
                )}
              </Box>
            )}
          />
        )}
      </Box>

      <Controller
        control={control}
        name={name('qty')}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value}
            label="Qty"
            sx={{ gridArea: 'qty' }}
            error={Boolean(err?.qty)}
            helperText={err?.qty}
            slotProps={{
              htmlInput: {
                inputMode: 'decimal',
                className: 'tabular',
                style: { textAlign: 'right' },
              },
            }}
          />
        )}
      />

      <TextField
        select
        label="Unit"
        value={units.some((u) => u.unitId === line.unitId) ? line.unitId : ''}
        onChange={(e) => changeUnit(e.target.value)}
        disabled={!line.variantId || units.length < 2}
        sx={{ gridArea: 'unit' }}
      >
        {units.map((u) => (
          <MenuItem key={u.unitId} value={u.unitId}>
            {u.symbol}
            {u.factor !== '1' && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                × {trimZeros(u.factor)}
              </Typography>
            )}
          </MenuItem>
        ))}
      </TextField>

      <Controller
        control={control}
        name={name('unitPrice')}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value}
            label="Price"
            sx={{ gridArea: 'price' }}
            error={Boolean(err?.unitPrice)}
            helperText={
              err?.unitPrice ??
              (list !== null && priceBelow ? `List ${money(list, format)}` : undefined)
            }
            slotProps={{
              htmlInput: {
                inputMode: 'decimal',
                className: 'tabular',
                style: { textAlign: 'right' },
              },
            }}
          />
        )}
      />

      <Box sx={{ gridArea: 'total', textAlign: 'right', pt: { md: 0.75 } }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: { md: 'none' } }}>
          Amount
        </Typography>
        <Typography className="tabular" sx={{ fontWeight: 700, fontSize: 16 }}>
          {money(result?.total ?? 0, format)}
        </Typography>
        {discounted && (
          <Typography
            variant="caption"
            className="tabular"
            sx={{ color: 'success.main', display: 'block' }}
          >
            −
            {money(
              Number(result?.lineDiscount ?? 0) + Number(result?.documentDiscount ?? 0),
              format,
            )}{' '}
            off
          </Typography>
        )}
        {Number(result?.tax ?? 0) > 0 && (
          <Typography
            variant="caption"
            className="tabular"
            color="text.secondary"
            sx={{ display: 'block' }}
          >
            incl. {money(result?.tax, format)} tax
          </Typography>
        )}
      </Box>

      <Box sx={{ gridArea: 'del', pt: 0.25 }}>
        <Tooltip title="Remove line">
          <span>
            <IconButton
              aria-label={`Remove line ${String(index + 1)}`}
              onClick={onRemove}
              disabled={!canRemove}
            >
              <DeleteOutlineRounded />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ gridArea: 'extra', alignItems: { sm: 'flex-start' } }}
      >
        <Controller
          control={control}
          name={name('discountValue')}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value}
              size="small"
              label="Line discount"
              placeholder="0"
              error={Boolean(err?.discount)}
              helperText={err?.discount}
              sx={{ width: { sm: 180 } }}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: {
                  inputMode: 'decimal',
                  className: 'tabular',
                  style: { textAlign: 'right' },
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Controller
                        control={control}
                        name={name('discountType')}
                        render={({ field: t }) => (
                          <DiscountTypeSwitch
                            value={t.value as DiscountType}
                            onChange={t.onChange}
                            currency={currency}
                          />
                        )}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
        {taxEnabled && (
          <Controller
            control={control}
            name={name('taxRateId')}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value}
                select
                size="small"
                label="Tax"
                sx={{ width: { sm: 180 } }}
              >
                <MenuItem value="">No tax</MenuItem>
                {taxRates
                  .filter((t) => t.isActive || t.id === field.value)
                  .map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name} · {trimZeros(t.rate)}%
                    </MenuItem>
                  ))}
              </TextField>
            )}
          />
        )}
        {needsPermission && (
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ alignItems: 'center', color: 'warning.main', pt: 0.75 }}
          >
            <WarningAmberRounded sx={{ fontSize: 18 }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Discounts and prices below list need the discount permission.
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
